import React, { useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Icon, type IconKind, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../../../../shared/hooks';
import type {
  Finding,
  KillChainPhase,
  LogEntry,
  SeverityLevel,
} from '../../data-layer/insightDashboardTypes';
import { parseFullTimestamp } from '../../data-layer/insightDashboardUtils';

import './InsightTimeline.css';

/* ------------------------------------------------------------------ */
/*  Types                                                             */
/* ------------------------------------------------------------------ */

interface InsightTimelineProps {
  logEntries: LogEntry[];
  findings: Finding[];
  sliceTimeRange?: { startEpoch: number; endEpoch: number } | null;
  phaseFilter?: KillChainPhase | 'ALL';
  severityFilter?: SeverityLevel | 'ALL';
  className?: string;
}

const defaultProps: Partial<InsightTimelineProps> = {
  sliceTimeRange: undefined,
  phaseFilter: undefined,
  severityFilter: undefined,
  className: undefined,
};

type BadgeType =
  | 'SESSION'
  | 'T-CODE'
  | 'BLOCKED'
  | 'EVENT'
  | 'USER MOD'
  | 'AUTH'
  | 'TABLE';

type FilterMode =
  | 'all'
  | 'findings'
  | 'events'
  | 'critical'
  | 'high'
  | 'medium';

interface TimelineEvent {
  kind: 'log';
  timestamp: string;
  epoch: number;
  badge: BadgeType;
  description: string;
  detail?: string;
  severity: string;
}

interface TimelineFinding {
  kind: 'finding';
  timestamp: string;
  epoch: number;
  finding: Finding;
  sessionId: string;
}

type TimelineItem = TimelineEvent | TimelineFinding;

/* ------------------------------------------------------------------ */
/*  Constants                                                         */
/* ------------------------------------------------------------------ */

const PAGE_SIZE = 30;

/* Dot colors — matching screenshot exactly */
const BADGE_DOT_COLORS: Record<BadgeType, string> = {
  SESSION: '#3b82f6',
  'T-CODE': '#6b7280',
  BLOCKED: '#ef4444',
  EVENT: '#6b7280',
  'USER MOD': '#ea580c',
  AUTH: '#d97706',
  TABLE: '#3b82f6',
};

/* Badge chip background */
const BADGE_BG_COLORS: Record<BadgeType, string> = {
  SESSION: '#dbeafe',
  'T-CODE': '#f3f4f6',
  BLOCKED: '#fee2e2',
  EVENT: '#f3f4f6',
  'USER MOD': '#fee2e2',
  AUTH: '#fef3c7',
  TABLE: '#dbeafe',
};

/* Badge chip text */
const BADGE_TEXT_COLORS: Record<BadgeType, string> = {
  SESSION: '#1d4ed8',
  'T-CODE': '#374151',
  BLOCKED: '#b91c1c',
  EVENT: '#374151',
  'USER MOD': '#b91c1c',
  AUTH: '#92400e',
  TABLE: '#1d4ed8',
};

const SEVERITY_COLORS: Record<string, string> = {
  CRITICAL: '#ef4444',
  HIGH: '#ea580c',
  MEDIUM: '#d97706',
  LOW: '#22c55e',
};

const RISK_LABEL = 'Risk';
const LOG_DETAILS = 'Log Details';
const SESSIONS_PREFIX = 'sessions (';
const VISIBLE_SUFFIX = 'visible)';
const INCIDENTS_LABEL = 'incidents';
const CRITICAL_LABEL = 'critical';
const HIGH_LABEL = 'high';
const MEDIUM_LABEL = 'medium';
const SHOW_ALL = 'Show all';
const REMAINING_EVENTS = 'remaining events';

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function classifyBadge(entry: LogEntry): BadgeType {
  const audit = entry.auditClass.toLowerCase() || '';
  const details = entry.details.toLowerCase() || '';

  if (details.includes('failed') || details.includes('denied')) {
    return 'BLOCKED';
  }

  if (audit.includes('dialog logon') || audit.includes('rfc/cpic logon')) {
    return 'SESSION';
  }

  if (audit.includes('table access') || details.includes('accessed')) {
    return 'TABLE';
  }

  if (audit.includes('user master')) {
    if (details.includes('authorization') || details.includes('profile')) {
      return 'AUTH';
    }

    return 'USER MOD';
  }

  if (audit.includes('transaction start')) {
    return 'T-CODE';
  }

  if (audit.includes('other event')) {
    return 'EVENT';
  }

  return 'EVENT';
}

function extractDescription(entry: LogEntry): {
  description: string;
  detail?: string;
} {
  const badge = classifyBadge(entry);
  const details = entry.details || '';

  if (badge === 'T-CODE') {
    const match = details.match(/Transaction\s+(\S+)\s+started/i);

    if (match) {
      return { description: match[1] };
    }

    return { description: entry.transactionCode || details };
  }

  if (badge === 'BLOCKED') {
    const match = details.match(/Start of transaction\s+(\S+)\s+failed/i);

    if (match) {
      const reasonMatch = details.match(/\(Reason=\d+\)/i);

      return {
        description: `${match[1]} blocked`,
        detail: `Authorization denied ${
          reasonMatch ? reasonMatch[0] : ''
        }`.trim(),
      };
    }

    return { description: details };
  }

  if (badge === 'SESSION') {
    const match = details.match(/^(Logon successful)\s*(\(.+\))$/i);

    if (match) {
      return { description: match[1], detail: match[2] };
    }

    return { description: details };
  }

  if (badge === 'TABLE') {
    const tableMatch = details.match(/Table\s+(\S+)\s+was displayed/i);

    if (tableMatch) {
      const activityMatch = details.match(/Activity\s+\d+/i);

      return {
        description: `Accessed ${tableMatch[1]}`,
        detail: activityMatch
          ? `${activityMatch[0]} via ${entry.transactionCode}`
          : undefined,
      };
    }

    return { description: details };
  }

  if (badge === 'USER MOD') {
    const createdMatch = details.match(/User\s+(\S+)\s+created/i);

    if (createdMatch) {
      return { description: `created user ${createdMatch[1]}` };
    }

    const changedMatch = details.match(
      /User(?:\s+master\s+record)?\s+(\S+)\s+changed/i
    );

    if (changedMatch) {
      return { description: `changed user ${changedMatch[1]}` };
    }

    return { description: details };
  }

  if (badge === 'AUTH') {
    const authMatch = details.match(
      /(Authorization|Profile)\s+(.+?)(?:\s+created|\s+changed)/i
    );

    if (authMatch) {
      return {
        description: `Created ${authMatch[1].toLowerCase()} ${authMatch[2]}`,
      };
    }

    return { description: details };
  }

  return { description: details };
}

function formatTimeFromTimestamp(ts: string): string {
  if (!ts || ts.length < 14) {
    return '';
  }

  const h = ts.slice(8, 10);
  const m = ts.slice(10, 12);
  const s = ts.slice(12, 14);

  return `${h}:${m}:${s}`;
}

function findingEpochFromSteps(
  finding: Finding,
  logEntries: LogEntry[]
): number {
  if (finding.steps.length > 0 && finding.steps[0].timestamp) {
    const ts = finding.steps[0].timestamp;
    const parts = ts.split(':');

    if (parts.length >= 3) {
      const h = parseInt(parts[0], 10);
      const m = parseInt(parts[1], 10);
      const s = parseInt(parts[2], 10);

      const refEntry = logEntries.find(
        (e) =>
          e.sessionId === finding.steps[0].sessionId &&
          parseFullTimestamp(e.startTimeUtc) > 0
      );

      if (refEntry) {
        const baseEpoch = parseFullTimestamp(refEntry.startTimeUtc);
        const baseDate = new Date(baseEpoch * 1000);
        const baseDayStart =
          new Date(
            baseDate.getFullYear(),
            baseDate.getMonth(),
            baseDate.getDate()
          ).getTime() / 1000;

        return baseDayStart + h * 3600 + m * 60 + s;
      }
    }
  }

  if (finding.evidenceLogIds?.length) {
    const firstEvidenceLogId = finding.evidenceLogIds[0];
    const entry = logEntries.find((e) => e.logId === firstEvidenceLogId);

    if (entry) {
      return parseFullTimestamp(
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty logCreationDateTime should fall back to startTimeUtc
        entry.logCreationDateTime || entry.startTimeUtc
      );
    }
  }

  return 0;
}

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

/**
 * Session-scoped log + finding timeline with filters, collapse, and navigation to audit sessions.
 */
function InsightTimeline({
  logEntries,
  findings,
  sliceTimeRange = null,
  phaseFilter = 'ALL',
  severityFilter = 'ALL',
  className,
}: InsightTimelineProps) {
  const history = useHistory();
  const l = useInvertedLocalize();

  const [isCollapsed, setIsCollapsed] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [filterMode, setFilterMode] = useState<FilterMode>('all');

  /* --- Session ID → Request ID lookup ----------------------------- */
  const sessionToRequestId = useMemo(() => {
    const map = new Map<string, string>();

    logEntries.forEach((entry) => {
      if (entry.sessionId && entry.requestId && !map.has(entry.sessionId)) {
        map.set(entry.sessionId, entry.requestId);
      }
    });

    return map;
  }, [logEntries]);

  const navigateToSession = useCallback(
    (sessionId: string, e?: React.SyntheticEvent) => {
      e?.stopPropagation();
      const requestId = sessionToRequestId.get(sessionId);

      if (requestId) {
        history.push(`/activitylog/${requestId}/view`);
      }
    },
    [sessionToRequestId, history]
  );

  const allItems: TimelineItem[] = useMemo(() => {
    const items: TimelineItem[] = [];

    logEntries.forEach((entry) => {
      const epoch = parseFullTimestamp(
        // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty logCreationDateTime should fall back to startTimeUtc
        entry.logCreationDateTime || entry.startTimeUtc
      );

      if (epoch <= 0) {
        return;
      }

      const badge = classifyBadge(entry);
      const { description, detail } = extractDescription(entry);

      items.push({
        kind: 'log',
        timestamp: formatTimeFromTimestamp(
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty logCreationDateTime should fall back to startTimeUtc
          entry.logCreationDateTime || entry.startTimeUtc
        ),
        epoch,
        badge,
        description,
        detail,
        severity: entry.severity,
      });
    });

    findings.forEach((finding) => {
      const epoch = findingEpochFromSteps(finding, logEntries);

      if (epoch <= 0) {
        return;
      }

      const sessionId = finding.steps[0]?.sessionId ?? '';
      const ts = finding.steps[0]?.timestamp ?? '';

      items.push({
        kind: 'finding',
        timestamp: ts,
        epoch,
        finding,
        sessionId,
      });
    });

    items.sort((a, b) => a.epoch - b.epoch);

    return items;
  }, [logEntries, findings]);

  /* --- Step 1: time + phase filter (used for chip counts) ---------- */
  const baseFilteredItems = useMemo(() => {
    let items = allItems;

    if (sliceTimeRange) {
      items = items.filter(
        (item) =>
          item.epoch >= sliceTimeRange.startEpoch &&
          item.epoch <= sliceTimeRange.endEpoch
      );
    }

    if (phaseFilter !== 'ALL') {
      items = items.filter(
        (item) => item.kind === 'log' || item.finding.phase === phaseFilter
      );
    }

    return items;
  }, [allItems, sliceTimeRange, phaseFilter]);

  /* --- Step 2: + severity filter (used for list display) ---------- */
  const slicedItems = useMemo(() => {
    if (severityFilter === 'ALL') {
      return baseFilteredItems;
    }

    return baseFilteredItems.filter(
      (item) => item.kind === 'log' || item.finding.severity === severityFilter
    );
  }, [baseFilteredItems, severityFilter]);

  /* --- Apply chip filter on top of sliced items ------------------- */
  const filteredItems = useMemo(() => {
    if (filterMode === 'findings') {
      return slicedItems.filter((item) => item.kind === 'finding');
    }

    if (filterMode === 'events') {
      return slicedItems.filter((item) => item.kind === 'log');
    }

    if (
      filterMode === 'critical' ||
      filterMode === 'high' ||
      filterMode === 'medium'
    ) {
      const sev = filterMode.toUpperCase();

      return slicedItems.filter(
        (item) => item.kind === 'finding' && item.finding.severity === sev
      );
    }

    return slicedItems;
  }, [slicedItems, filterMode]);

  /* --- Chip stats (derived from base — before severity filter) ---- */
  const totalEvents = baseFilteredItems.filter((i) => i.kind === 'log').length;
  const totalFindings = baseFilteredItems.filter(
    (i) => i.kind === 'finding'
  ).length;

  const severityCounts = useMemo(() => {
    const findingItems = baseFilteredItems.filter(
      (item): item is TimelineFinding => item.kind === 'finding'
    );

    return {
      critical: findingItems.filter(
        (item) => item.finding.severity === 'CRITICAL'
      ).length,
      high: findingItems.filter((item) => item.finding.severity === 'HIGH')
        .length,
      medium: findingItems.filter((item) => item.finding.severity === 'MEDIUM')
        .length,
    };
  }, [baseFilteredItems]);

  const sessionCount = useMemo(() => {
    if (sliceTimeRange) {
      const sids = logEntries
        .filter((e) => {
          if (!e.sessionId) {
            return false;
          }

          const epoch = parseFullTimestamp(
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty logCreationDateTime should fall back to startTimeUtc
            e.logCreationDateTime || e.startTimeUtc
          );

          return (
            epoch >= sliceTimeRange.startEpoch &&
            epoch <= sliceTimeRange.endEpoch
          );
        })
        .map((e) => e.sessionId);

      return new Set(sids).size;
    }

    return new Set(
      logEntries.filter((e) => e.sessionId).map((e) => e.sessionId)
    ).size;
  }, [logEntries, sliceTimeRange]);

  const visibleItems = filteredItems.slice(0, visibleCount);
  const hasMore = visibleCount < filteredItems.length;
  const remainingCount = filteredItems.length - visibleCount;

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const handleShowMore = useCallback(() => {
    setVisibleCount((prev) => prev + PAGE_SIZE);
  }, []);

  const handleFilterClick = useCallback(
    (mode: FilterMode, e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setFilterMode((prev) => (prev === mode ? 'all' : mode));
      setVisibleCount(PAGE_SIZE);
    },
    []
  );

  /* ---------------------------------------------------------------- */
  /*  Render: Log event card                                          */
  /* ---------------------------------------------------------------- */

  const renderLogEvent = (item: TimelineEvent, index: number) => (
    <div key={`log-${index}`} className='Sod-InsightTimeline-card'>
      <span className='Sod-InsightTimeline-card-time'>{item.timestamp}</span>
      <span
        className='Sod-InsightTimeline-card-dot'
        style={{ backgroundColor: BADGE_DOT_COLORS[item.badge] }}
      />
      <span
        className='Sod-InsightTimeline-card-badge'
        style={{
          backgroundColor: BADGE_BG_COLORS[item.badge],
          color: BADGE_TEXT_COLORS[item.badge],
        }}>
        {item.badge}
      </span>
      <span className='Sod-InsightTimeline-card-text'>{item.description}</span>
      {item.detail && (
        <span className='Sod-InsightTimeline-card-detail'>{item.detail}</span>
      )}
    </div>
  );

  /* ---------------------------------------------------------------- */
  /*  Render: Finding card                                            */
  /* ---------------------------------------------------------------- */

  const renderFindingEvent = (item: TimelineFinding, index: number) => {
    const { finding } = item;
    const sevColor: string =
      SEVERITY_COLORS[finding.severity] || SEVERITY_COLORS.LOW;

    return (
      <div key={`finding-${index}`} className='Sod-InsightTimeline-findingCard'>
        <div
          className='Sod-InsightTimeline-findingCard-accent'
          style={{ backgroundColor: sevColor }}
        />
        <div className='Sod-InsightTimeline-findingCard-inner'>
          <div className='Sod-InsightTimeline-findingCard-row1'>
            <span className='Sod-InsightTimeline-findingCard-time'>
              {item.timestamp}
            </span>
            <span className='Sod-InsightTimeline-findingCard-icon'>
              <Icon
                kind={'WarningOutline' as unknown as IconKind}
                size='small'
                color={sevColor}
              />
            </span>
            <span
              className='Sod-InsightTimeline-findingCard-severity'
              style={{ color: sevColor }}>
              {finding.severity}
            </span>
            <span className='Sod-InsightTimeline-findingCard-risk'>
              {RISK_LABEL} {finding.riskScore}
            </span>
            <button
              type='button'
              className='Sod-InsightTimeline-findingCard-session Sod-InsightTimeline-sessionLink'
              aria-label={l('Open session {sessionId}', {
                sessionId: item.sessionId,
              })}
              onClick={(e) => navigateToSession(item.sessionId, e)}>
              {/* eslint-disable-next-line react/jsx-curly-brace-presence */}
              {'Session '}
              {item.sessionId}
            </button>
          </div>
          <div className='Sod-InsightTimeline-findingCard-title'>
            {finding.title}
          </div>
        </div>
      </div>
    );
  };

  /* ---------------------------------------------------------------- */
  /*  Render: Main                                                    */
  /* ---------------------------------------------------------------- */

  return (
    <Box className={cx('Sod-InsightTimeline', className)}>
      {/* Header */}
      <div className='Sod-InsightTimeline-header'>
        <div className='Sod-InsightTimeline-headerTop'>
          <Typography kind='h3' className='Sod-InsightTimeline-title'>
            {LOG_DETAILS}
          </Typography>
          <button
            type='button'
            className={cx('Sod-InsightTimeline-chevron', {
              'is-collapsed': isCollapsed,
            })}
            aria-expanded={!isCollapsed}
            aria-label={l('Expand or collapse log details')}
            onClick={handleToggleCollapse}>
            <Icon
              kind={'ChevronUpOutline' as unknown as IconKind}
              size='small'
            />
          </button>
        </div>
        <div className='Sod-InsightTimeline-chips'>
          <button
            type='button'
            className={cx(
              'Sod-InsightTimeline-chip Sod-InsightTimeline-chip--sessions',
              {
                'is-active': filterMode === 'events',
              }
            )}
            aria-pressed={filterMode === 'events'}
            onClick={(e) => handleFilterClick('events', e)}>
            {sessionCount} {SESSIONS_PREFIX}
            {totalEvents} {VISIBLE_SUFFIX}
          </button>
          <button
            type='button'
            className={cx(
              'Sod-InsightTimeline-chip Sod-InsightTimeline-chip--incidents',
              {
                'is-active': filterMode === 'findings',
              }
            )}
            aria-pressed={filterMode === 'findings'}
            onClick={(e) => handleFilterClick('findings', e)}>
            {totalFindings} {INCIDENTS_LABEL}
          </button>
          {severityCounts.critical > 0 && (
            <button
              type='button'
              className={cx(
                'Sod-InsightTimeline-chip Sod-InsightTimeline-chip--critical',
                {
                  'is-active': filterMode === 'critical',
                }
              )}
              aria-pressed={filterMode === 'critical'}
              onClick={(e) => handleFilterClick('critical', e)}>
              {severityCounts.critical} {CRITICAL_LABEL}
            </button>
          )}
          {severityCounts.high > 0 && (
            <button
              type='button'
              className={cx(
                'Sod-InsightTimeline-chip Sod-InsightTimeline-chip--high',
                {
                  'is-active': filterMode === 'high',
                }
              )}
              aria-pressed={filterMode === 'high'}
              onClick={(e) => handleFilterClick('high', e)}>
              {severityCounts.high} {HIGH_LABEL}
            </button>
          )}
          {severityCounts.medium > 0 && (
            <button
              type='button'
              className={cx(
                'Sod-InsightTimeline-chip Sod-InsightTimeline-chip--medium',
                {
                  'is-active': filterMode === 'medium',
                }
              )}
              aria-pressed={filterMode === 'medium'}
              onClick={(e) => handleFilterClick('medium', e)}>
              {severityCounts.medium} {MEDIUM_LABEL}
            </button>
          )}
        </div>
      </div>

      {/* Event list */}
      {!isCollapsed && (
        <div className='Sod-InsightTimeline-list'>
          {visibleItems.map((item, index) =>
            item.kind === 'log'
              ? renderLogEvent(item, index)
              : renderFindingEvent(item, index)
          )}

          {hasMore && (
            <button
              type='button'
              className='Sod-InsightTimeline-showMore'
              onClick={handleShowMore}>
              <Icon
                kind={'ChevronDownOutline' as unknown as IconKind}
                size='small'
              />
              <span>
                {SHOW_ALL} {remainingCount} {REMAINING_EVENTS}
              </span>
            </button>
          )}
        </div>
      )}
    </Box>
  );
}

InsightTimeline.defaultProps = defaultProps;

export default InsightTimeline;
